import "./path/context.ts"; // We have some cycles, this ensures correct order to avoid TDZ
import * as visitors from "./visitors.ts";
import {
  VISITOR_KEYS,
  removeProperties,
  type RemovePropertiesOptions,
  traverseFast,
} from "@babel/types";
import type * as t from "@babel/types";
import * as cache from "./cache.ts";
import type NodePath from "./path/index.ts";
import type { default as Scope, Binding } from "./scope/index.ts";
import type {
  ExplodedVisitor,
  Visitor,
  VisitorBase,
  VisitorProp,
  TraverseOptions,
} from "./types.ts";
import { traverseNode } from "./traverse-node.ts";

export type { ExplodedVisitor, Visitor, VisitorBase, Binding, TraverseOptions };
export { default as NodePath } from "./path/index.ts";
export { default as Scope, type BindingKind } from "./scope/index.ts";
export { default as Hub } from "./hub.ts";
export type { HubInterface } from "./hub.ts";
export type { VisitWrapper } from "./visitors.ts";

export { visitors };

function traverse<S, T extends object>(
  parent: t.Node,
  opts: {
    [P in keyof T]: VisitorProp<S, P & string>;
  },
  scope: Scope | null | undefined,
  state: S,
  parentPath?: NodePath,
  visitSelf?: boolean,
): void;
function traverse<T extends object>(
  parent: t.Node,
  opts: {
    [P in keyof T]: VisitorProp<any, P & string>;
  },
  scope?: Scope | null,
  state?: any,
  parentPath?: NodePath,
  visitSelf?: boolean,
): void;
function traverse<S>(
  parent: t.Node,
  opts: TraverseOptions & Visitor<S>,
  scope: Scope | null | undefined,
  state: S,
  parentPath?: NodePath,
  visitSelf?: boolean,
): void;
function traverse(
  parent: t.Node,
  opts: TraverseOptions & Visitor<any>,
  scope?: Scope | null,
  state?: any,
  parentPath?: NodePath,
  visitSelf?: boolean,
): void;
function traverse(
  parent: t.Node,
  opts: any = {},
  scope?: Scope | null,
  state?: any,
  parentPath?: NodePath,
  visitSelf?: boolean,
) {
  if (!parent) return;

  if (!opts.noScope && !scope) {
    if (parent.type !== "Program" && parent.type !== "File") {
      throw new Error(
        "You must pass a scope and parentPath unless traversing a Program/File. " +
          `Instead of that you tried to traverse a ${parent.type} node without ` +
          "passing scope and parentPath.",
      );
    }
  }

  if (!parentPath && visitSelf) {
    throw new Error("visitSelf can only be used when providing a NodePath.");
  }

  if (!VISITOR_KEYS[parent.type]) {
    return;
  }

  visitors.explode(opts);

  traverseNode(
    parent,
    opts,
    scope,
    state,
    parentPath,
    /* skipKeys */ undefined,
    visitSelf,
  );
}

export default traverse;

traverse.visitors = visitors;
traverse.verify = visitors.verify;
traverse.explode = visitors.explode;

traverse.cheap = function (node: t.Node, enter: (node: t.Node) => void) {
  traverseFast(node, enter);
  return;
};

traverse.node = function (
  node: t.Node,
  opts: TraverseOptions & ExplodedVisitor,
  scope?: Scope,
  state?: any,
  path?: NodePath,
  skipKeys?: Record<string, boolean>,
) {
  traverseNode(node, opts, scope, state, path, skipKeys);
  // traverse.node always returns undefined
};

traverse.clearNode = function (node: t.Node, opts?: RemovePropertiesOptions) {
  removeProperties(node, opts);
};

traverse.removeProperties = function (
  tree: t.Node,
  opts?: RemovePropertiesOptions,
) {
  traverseFast(tree, traverse.clearNode, opts);
  return tree;
};

traverse.hasType = function (
  tree: t.Node,
  type: t.Node["type"],
  denylistTypes?: string[],
): boolean {
  // the node we're searching in is denylisted
  if (denylistTypes?.includes(tree.type)) return false;

  // the type we're looking for is the same as the passed node
  if (tree.type === type) return true;

  return traverseFast(tree, function (node) {
    if (denylistTypes?.includes(node.type)) {
      return traverseFast.skip;
    }
    if (node.type === type) {
      return traverseFast.stop;
    }
  });
};

traverse.cache = cache;
